if (!customElements.get('testimonial-stories')) {
  /*
   * Story-style testimonials.
   * Autoplay (muted) while the section is in view: videos play to the end, image cards hold for
   * data-ts-hold ms, then the next story starts (wrapping to the first). Tap the active card to
   * pause/resume, mute button toggles sound for all videos. Swipe/arrows/segments navigate.
   */
  class TestimonialStories extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ts-track]');
      this.cards = Array.from(this.querySelectorAll('[data-ts-card]'));
      this.quotes = Array.from(this.querySelectorAll('[data-ts-quote]'));
      this.segs = Array.from(this.querySelectorAll('[data-ts-seg]'));
      this.prevBtn = this.querySelector('[data-ts-prev]');
      this.nextBtn = this.querySelector('[data-ts-next]');
      this.currentEl = this.querySelector('[data-ts-current]');
      if (!this.track || !this.cards.length) return;

      this.index = 0;
      this.muted = true;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.paused = this.reducedMotion;
      this.inView = false;
      this.holdElapsed = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onSettle = this.onSettle.bind(this);
      this.tick = this.tick.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      // finger on the track: freeze autoplay so an auto-advance never fights the swipe
      this.track.addEventListener('touchstart', () => {
        this.touching = true;
        this.halt();
      }, { passive: true });
      const release = () => {
        this.touching = false;
        this.onScroll();
      };
      this.track.addEventListener('touchend', release, { passive: true });
      this.track.addEventListener('touchcancel', release, { passive: true });

      this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));
      this.segs.forEach((seg) => seg.addEventListener('click', () => this.goTo(Number(seg.dataset.tsSeg))));

      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
        if ((e.key === ' ' || e.key === 'Enter') && e.target === this.track && this.cards[this.index].hasAttribute('data-ts-video')) { e.preventDefault(); this.togglePause(); }
      });

      this.cards.forEach((card, i) => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('[data-ts-mute]')) return;
          if (i !== this.index) this.goTo(i);
          else if (card.hasAttribute('data-ts-video')) this.togglePause();
        });
        card.querySelector('[data-ts-mute]')?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.setMuted(!this.muted);
        });

        const video = card.querySelector('video');
        if (!video) return;
        video.addEventListener('loadedmetadata', () => this.setDuration(card, video));
        video.addEventListener('timeupdate', () => {
          if (i === this.index && video.duration) this.setProgress(video.currentTime / video.duration);
        });
        video.addEventListener('play', () => card.classList.add('is-playing'));
        video.addEventListener('pause', () => card.classList.remove('is-playing'));
        video.addEventListener('ended', () => { if (i === this.index) this.advance(); });
      });

      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.inView ? this.run() : this.halt();
      }, { threshold: 0.4 });
      this.observer.observe(this.track);

      this.setActive(0, true);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.track?.removeEventListener('scroll', this.onScroll);
      cancelAnimationFrame(this.holdRaf);
      clearTimeout(this.settleTimer);
    }

    /* ---------- navigation ---------- */

    get step() {
      return this.cards.length > 1 ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : 1;
    }

    // only pick the active card once scrolling has come to rest – switching mid-swipe rescales
    // the cards under the finger and restarts videos on every card passed
    onScroll() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.onSettle, 120);
    }

    onSettle() {
      if (this.touching) return;
      const i = Math.round(this.track.scrollLeft / this.step);
      if (i !== this.index) this.setActive(i);
      else this.run();
    }

    goTo(i) {
      if (i < 0 || i >= this.cards.length) return;
      // scroll the track only — never the page
      this.track.scrollTo({ left: i * this.step, behavior: 'smooth' });
      this.setActive(i);
    }

    advance() {
      this.goTo(this.index + 1 < this.cards.length ? this.index + 1 : 0);
    }

    setActive(i, initial = false) {
      i = Math.max(0, Math.min(this.cards.length - 1, i));
      if (i === this.index && !initial) return;
      this.halt();
      const prev = this.cards[this.index]?.querySelector('video');
      if (prev && !initial) prev.currentTime = 0;
      this.index = i;
      this.holdElapsed = 0;
      if (!initial && !this.reducedMotion) {
        this.paused = false;
        this.classList.remove('is-paused');
      }
      this.setProgress(0);

      this.cards.forEach((card, n) => {
        card.classList.toggle('is-active', n === i);
        card.classList.toggle('is-next', n === i + 1);
        card.classList.toggle('is-prev', n < i);
        card.setAttribute('aria-current', n === i ? 'true' : 'false');
      });
      this.quotes.forEach((q, n) => {
        q.classList.toggle('is-active', n === i);
        q.setAttribute('aria-hidden', n === i ? 'false' : 'true');
      });
      this.segs.forEach((s, n) => {
        s.classList.toggle('is-active', n === i);
        s.classList.toggle('is-done', n < i);
      });
      if (this.currentEl) this.currentEl.textContent = i + 1;
      if (this.prevBtn) this.prevBtn.disabled = i === 0;
      if (this.nextBtn) this.nextBtn.disabled = i === this.cards.length - 1;

      // only the active and next video fetch anything
      [i, i + 1].forEach((n) => {
        const v = this.cards[n]?.querySelector('video');
        if (v && v.preload === 'none') v.preload = 'metadata';
      });

      this.run();
    }

    /* ---------- playback ---------- */

    run() {
      if (!this.inView || this.paused) return;
      const card = this.cards[this.index];
      const video = card.querySelector('video');
      this.classList.remove('is-paused');
      if (video) {
        video.muted = this.muted;
        const blocked = () => {
          // autoplay blocked (e.g. iOS low power mode) → fall back to tap-to-play
          this.paused = true;
          this.classList.add('is-paused');
        };
        video.play().catch(() => {
          if (this.muted) return blocked();
          // unmuted autoplay refused outside a user gesture → retry muted
          this.setMuted(true);
          video.play().catch(blocked);
        });
      } else {
        this.holdStart = performance.now() - this.holdElapsed;
        this.holdDuration = Number(card.dataset.tsHold) || 4000;
        cancelAnimationFrame(this.holdRaf);
        this.holdRaf = requestAnimationFrame(this.tick);
      }
    }

    tick(now) {
      this.holdElapsed = now - this.holdStart;
      const p = this.holdElapsed / this.holdDuration;
      this.setProgress(p);
      if (p >= 1) return this.advance();
      this.holdRaf = requestAnimationFrame(this.tick);
    }

    halt() {
      cancelAnimationFrame(this.holdRaf);
      this.cards.forEach((c) => {
        const v = c.querySelector('video');
        if (v && !v.paused) v.pause();
      });
    }

    togglePause() {
      this.paused = !this.paused;
      this.classList.toggle('is-paused', this.paused);
      this.paused ? this.halt() : this.run();
    }

    setMuted(muted) {
      this.muted = muted;
      this.classList.toggle('is-unmuted', !muted);
      this.cards.forEach((c) => {
        const v = c.querySelector('video');
        if (v) v.muted = muted;
        const b = c.querySelector('[data-ts-mute]');
        if (b) {
          b.setAttribute('aria-pressed', String(!muted));
          b.setAttribute('aria-label', muted ? 'Ton einschalten' : 'Ton ausschalten');
        }
      });
      // unmuting is a clear "I'm watching" signal → resume if paused
      if (!muted && this.paused) this.togglePause();
    }

    setProgress(p) {
      this.style.setProperty('--ts-progress', `${Math.min(1, Math.max(0, p)) * 100}%`);
    }

    setDuration(card, video) {
      const el = card.querySelector('[data-ts-duration]');
      if (!el || el.textContent.trim() || !isFinite(video.duration)) return;
      const s = Math.round(video.duration);
      el.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    }
  }

  customElements.define('testimonial-stories', TestimonialStories);
}
